/**
 * FASE BRASIL P1.5 — Central configuration for the Brazil experience.
 *
 * Toggles every Brazil-specific surface so the entire experience can be
 * enabled, disabled or extended without touching components.
 *
 * NO hardcoded behaviour in components: every conditional reads from
 * `getBrazilConfig()` or the `useBrazilMode()` hook.
 */

export interface BrazilExperienceConfig {
  /** Master switch — if false, nothing Brazil-specific renders. */
  enabled: boolean;
  /** Force Brazil mode regardless of geo (QA / demos / non-BR previews). */
  forceBrazilMode: boolean;
  /** Branding overrides applied when active. */
  branding: {
    productName: string;
    tagline: string;
    flagEmoji: string;
  };
  /** Toggles per Brasil Hub surface. */
  surfaces: {
    cazeTv: boolean;
    ranking: boolean;
    missions: boolean;
    bettingTeaser: boolean;
    influencersTeaser: boolean;
  };
  /** Extension points reserved for upcoming phases. */
  extensions: {
    /** Filled in P2 — betting partner integrations. */
    bettingProvider: null | string;
    /** Filled in P2 — influencer roster source. */
    influencerSource: null | string;
  };
}

export const DEFAULT_BRAZIL_CONFIG: BrazilExperienceConfig = {
  enabled: true,
  forceBrazilMode: false,
  branding: {
    productName: "VerseSport Brasil™",
    tagline: "A segunda tela do futebol brasileiro",
    flagEmoji: "🇧🇷",
  },
  surfaces: {
    cazeTv: true,
    ranking: true,
    missions: true,
    bettingTeaser: true,
    influencersTeaser: false,
  },
  extensions: {
    bettingProvider: null,
    influencerSource: null,
  },
};

const STORAGE_KEY = "vs:brazil_config_v1";
const FORCE_KEY = "vs:force_brazil_mode";

function safeParse(raw: string | null): Partial<BrazilExperienceConfig> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<BrazilExperienceConfig>;
  } catch {
    return null;
  }
}

/**
 * Returns the merged Brazil experience config.
 * Reads optional localStorage overrides so QA and tenants can toggle
 * surfaces without a redeploy. SSR-safe.
 */
export function getBrazilConfig(): BrazilExperienceConfig {
  if (typeof window === "undefined") return DEFAULT_BRAZIL_CONFIG;
  const override = safeParse(window.localStorage.getItem(STORAGE_KEY));
  const forced = window.localStorage.getItem(FORCE_KEY) === "1";
  const merged: BrazilExperienceConfig = {
    ...DEFAULT_BRAZIL_CONFIG,
    ...(override ?? {}),
    forceBrazilMode: forced || override?.forceBrazilMode || DEFAULT_BRAZIL_CONFIG.forceBrazilMode,
    branding: { ...DEFAULT_BRAZIL_CONFIG.branding, ...(override?.branding ?? {}) },
    surfaces: { ...DEFAULT_BRAZIL_CONFIG.surfaces, ...(override?.surfaces ?? {}) },
    extensions: { ...DEFAULT_BRAZIL_CONFIG.extensions, ...(override?.extensions ?? {}) },
  };
  return merged;
}

export function setBrazilConfigOverride(
  override: Partial<BrazilExperienceConfig> | null,
): void {
  if (typeof window === "undefined") return;
  if (!override) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(override));
}

export function setForceBrazilMode(force: boolean): void {
  if (typeof window === "undefined") return;
  if (force) window.localStorage.setItem(FORCE_KEY, "1");
  else window.localStorage.removeItem(FORCE_KEY);
}